import { _axios } from '@/plugins/axios'
import rc from '@/plugins/converters/resources'

/**
 * @typedef {Object} ResourcesState
 * @property resourceDisplayed {ResourceAir} Ressource affichée dans la modale lorsqu'on clique sur la ressource.
 * @property resourcesDiscarded {ResourceAir[]} Tableau pour afficher les ressources marquées "désactivée".
 * @property resourcesCompleted {ResourceAir[]} Tableau pour afficher les ressources marquées "terminée".
 * @property resources {ResourceAir[]} Liste des ressources.
 *
 * @type {ResourcesState}
 */
const state = {
  resourceDisplayed: {},
  resourcesDiscarded: [],
  resourcesCompleted: [],
  /**
   * @typedef {Object} AttendanceAir
   * @property {Date|null} completionDate - Date où l'utilisateur a fini la ressource
   * @property {Boolean} isCompleted - Indique si l'utilisateur a fini la ressource
   * @property {Boolean} isDiscarded - Indique si l'utilisateur ne veut plus voir la ressource
   */
  /**
   * @typedef {Object} ResourceAir
   * @property {string} id - Identifiant de la ressource
   * @property {AttendanceAir} attendance - Informations quant à l'état de complétude de la ressource
   * @property {String} author - Auteur de la ressource
   * @property {String} description - Description de la ressource
   * @property {string} duration - Temps moyen pour finir la ressource
   * @property {string} format - Type de ressource
   * @property {string} icon - Nom de l'icône Jalios associée à la ressource
   * @property {string} image - URL de l'image d'en-tête de la ressource
   * @property {string} isActive - Indique si la ressource est active
   * @property {string} language - Langue de la ressource
   * @property {string} level - Niveau défini pour la ressource
   * @property {string} link - URL pour se rendre dans la ressource dans le catalogue de Jalios
   * @property {string} nature - Nature de la ressource
   * @property {string} publicationDate - Date de publication de la ressource
   * @property {string} title - Titre de la ressource
   * @type {ResourceAir[]}
   */
  resources: []
}

const getters = {
  resourceDisplayed: state => state.resourceDisplayed,
  resourcesDiscarded: state => state.resourcesDiscarded,
  resourcesCompleted: state => state.resourcesCompleted,
  resources: state => state.resources
}

const actions = {
  displayResource: ({ commit }, resource) => {
    commit('SELECT', resource)
  },
  loadAdditionalResources: ({ commit, dispatch }, { type, questionnaireId }) => {
    return _axios.get(`user/questionnaires/${questionnaireId}/get_${type}/`)
      .then(response => {
        commit('LOAD_ADDITIONAL_RESOURCES', { type, resources: response.data })
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  loadResources: ({ commit, dispatch }, questionnaireId) => {
    return _axios.get(`user/questionnaires/${questionnaireId}/get_resources/`)
      .then(response => {
        commit('LOAD_RESOURCES', response.data)
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  },
  updateTracker: ({ commit, dispatch }, { resourceId, status }) => {
    return _axios.put(`user/resources/${resourceId}/${status}/`)
      .then(response => {
        commit('UPDATE_TRACK', { resourceUpdated: response.data, status })
      })
      .catch(error => {
        dispatch('displayLoadError', error, { root: true })
      })
  }
}

const mutations = {
  LOAD_ADDITIONAL_RESOURCES: (state, { type, resources }) => {
    type === 'discarded'
      ? state.resourcesDiscarded = resources.map(resource => rc.DJToJS.convertResource(resource))
      : state.resourcesCompleted = resources.map(resource => rc.DJToJS.convertResource(resource))
  },
  LOAD_RESOURCES: (state, resources) => {
    state.resources = resources.map(resource => rc.DJToJS.convertResource(resource))
  },
  SELECT: (state, resource) => {
    state.resourceDisplayed = Object.assign({}, resource)
  },
  UPDATE_TRACK: (state, { resourceUpdated, status }) => {
    status === 'complete'
      ? state.resourcesCompleted.push(rc.DJToJS.convertResource(resourceUpdated))
      : state.resourcesDiscarded.push(rc.DJToJS.convertResource(resourceUpdated))
    state.resources = state.resources.filter(resource => resource.id !== resourceUpdated.id)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
